import { useState, useEffect, useCallback } from 'react';
import { useField } from 'formik';
import { InputGroup, Box, Flex } from '@chakra-ui/react';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { editMe } from '@/store/me/actions';
import { selectUser } from '@/store/bootstrap/selectors';
// import 'react-datepicker/dist/react-datepicker.css';
// import 'react-datepicker/dist/react-datepicker-cssmodules.css';
// import "react-day-picker/style.css";
// import './customDateField.css';
import './daypickerV1.css';
import { TimeZoneSelectorV1 } from './components/TimeZoneSelectorV1';
import { CustomTimeSelectorTz } from './components/CustomTimeSelectorTz';
import { DayPicker } from 'react-day-picker';
import { selectMeForUpdate } from '@/store/me';
import { convertDateFormatForDatePicker, getDefaultTimeZone } from './helpers';
import { KText } from '@/ui-library/components';
import { useDateFormat, getDateFormat } from '@/hooks/useDateFormat';

export const DateFieldWithTZ = ({ item }) => {
  const {
    name,
    className,
    isRequired,
    enableTime = false,
    disablePreviousDate = false,
    customOnChange = () => {},
    showError = true,
  } = item || {};
  const { time_zones } = useSelector(selectMeForUpdate) || {};

  const currUser = useSelector(selectUser);
  //To get the default user Time-format(12hr or 24 hr) and date-format
  const { time_fmt, date_fmt, time_zone } = currUser || {};

  const [selectedDate, setSelectedDate] = useState();
  const [selectedTime, setSelectedTime] = useState();
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    return getDefaultTimeZone(time_zone);
  });
  const [dateWrapper, setDateWrapper] = useState({});

  // To set the meta values(edit-values) using formik helpers
  const [, meta, helpers] = useField(name);

  //setValue to return the selected values to component
  const { setValue, setTouched } = helpers || {};

  const { value } = meta || {};

  const getTimeFormat = () => {
    return time_fmt === 'hour_12' ? 'hh:mm A' : 'HH:mm';
  };

  // variable store selected-timezone date(starting date in that selected timezone)

  const dispatch = useDispatch();

  useEffect(() => {
    if (enableTime) {
      dispatch(editMe({}));
    }
  }, [dispatch, enableTime]);

  // Function to check if a date is the current day in the selected timezone
  const isCurrentDay = useCallback(
    (date) => {
      if (!date || !selectedTimezone) return false;

      const currentDateInTZ = moment().tz(selectedTimezone.key);
      const dateToCheck = moment(date).tz(selectedTimezone.key, true);

      return (
        dateToCheck.format('YYYY-MM-DD') ===
        currentDateInTZ.format('YYYY-MM-DD')
      );
    },
    [selectedTimezone]
  );

  // set selected date, time and timeZone values
  const handleDateChange = (date) => {
    setSelectedDate(date);

    // If selected date is current day in the selected timezone and no time selected yet,
    // update time to current time plus buffer
    if (date && selectedTimezone && isCurrentDay(date) && !selectedTime) {
      const nowInSelectedTimezone = moment().tz(selectedTimezone.key);
      const roundedMinutes =
        Math.ceil(nowInSelectedTimezone.minutes() / 15) * 15;
      nowInSelectedTimezone.minutes(roundedMinutes).seconds(0);
      setSelectedTime(nowInSelectedTimezone.format('HH:mm'));
    }
  };
  // Function to handle time change
  const handleTimeChange = (time) => {
    setSelectedTime(time);
  };
  //Function-handles-time-zone-change
  const handleTimeZoneChange = (zone) => {
    setSelectedTimezone(zone);

    if (!selectedDate) return;

    const nowInNewTimezone = moment().tz(zone.key);

    // Check if selected date is in the past in the new timezone
    const selectedDateInNewTZ = moment(selectedDate).tz(zone.key, true);

    // If selected date is in the past in new timezone, update it to current date
    if (
      selectedDateInNewTZ.startOf('day').valueOf() <
      nowInNewTimezone.startOf('day').valueOf()
    ) {
      setSelectedDate(nowInNewTimezone.toDate());
      // Only reset time if date was changed to current date and no time was selected
      if (!selectedTime) {
        const roundedMinutes = Math.ceil(nowInNewTimezone.minutes() / 15) * 15;
        nowInNewTimezone.minutes(roundedMinutes).seconds(0);
        setSelectedTime(nowInNewTimezone.format('HH:mm'));
      }
    }
  };

  const setFinalFormattedDate = () => {
    let updatedDate = moment(selectedDate);

    if (selectedTimezone !== null && Object.keys(selectedTimezone)) {
      updatedDate = updatedDate.tz(selectedTimezone.key, true);
    }

    if (selectedTime) {
      let selectedTimeSplited = selectedTime.split(':');
      updatedDate.set({
        hour: Number(selectedTimeSplited[0]),
        minute: Number(selectedTimeSplited[1]),
      });
    }
    let dateWrapperItems = {
      formattedTime: updatedDate.format('YYYY-MM-DDTHH:mm:ssZ'),
      time: selectedTime,
      timezoneValue: selectedTimezone,
      isFutureDate: hasFutureDate(),
    };
    setDateWrapper(dateWrapperItems);
    setValue(dateWrapperItems);
    setTouched(true);
    customOnChange(dateWrapperItems);
  };

  const hasFutureDate = () => {
    if (!selectedDate || !selectedTime || !selectedTimezone) return false;

    const currentTimeInSelectedTZ = moment().tz(selectedTimezone.key);

    // Parse selected time
    const [hour, minute] = selectedTime?.split(':').map(Number);

    // Create a moment with the selected date and time in the selected timezone
    const selectedMoment = moment(selectedDate)
      .tz(selectedTimezone.key, true)
      .set({
        hour: parseInt(hour, 10),
        minute: parseInt(minute, 10),
        second: 0,
      });

    // Check if the selected date and time are in the future
    return selectedMoment.isAfter(currentTimeInSelectedTZ);
  };

  // useEffect to update form values when any dependency changes
  useEffect(() => {
    if (selectedDate && selectedTime && selectedTimezone) {
      setFinalFormattedDate();
    }
    // eslint-disable-next-line
  }, [selectedDate, selectedTime, selectedTimezone]);

  // Additional useEffect to ensure time is valid when timezone changes
  // Only update time if no time is selected yet, don't override user's selection
  useEffect(() => {
    if (
      selectedTimezone &&
      selectedDate &&
      isCurrentDay(selectedDate) &&
      !selectedTime
    ) {
      // Get current time in new timezone
      const nowInSelectedTZ = moment().tz(selectedTimezone.key);
      const roundedMinutes = Math.ceil(nowInSelectedTZ.minutes() / 15) * 15;
      nowInSelectedTZ.minutes(roundedMinutes).seconds(0);

      // Update time if needed
      const newTime = nowInSelectedTZ.format('HH:mm');
      setSelectedTime(newTime);
    }
  }, [isCurrentDay, selectedDate, selectedTimezone, selectedTime]);

  // function to disable the past days in the caleander
  const disablePreviousDates = (date) => {
    if (!selectedTimezone || !selectedTimezone.key) return false;

    // Get current date in the selected timezone
    const currentDateInSelectedTZ = moment().tz(selectedTimezone.key);

    // Convert the date to be checked to the selected timezone
    const dateToCheck = moment(date).tz(selectedTimezone.key, true);

    // Compare dates after setting both to start of day in the selected timezone
    return (
      dateToCheck.startOf('day').valueOf() <
      currentDateInSelectedTZ.startOf('day').valueOf()
    );
  };

  const getCurrentTimePlus30 = (time = null) => {
    let now = moment().tz(currUser?.time_zone || moment.tz.guess());

    if (time) {
      // which means data is coming from server
      let updatedT = moment.parseZone(time).format('HH:mm');
      return updatedT;
    }
    const roundedMinutes = Math.ceil(now.minutes() / 30) * 30;
    // Set the rounded minutes and reset seconds to 0
    now.minutes(roundedMinutes).seconds(0);
    // Format in 24-hour format (HH:mm)
    return now.format('HH:mm');
  };

  // Function to filter time options to remove past times if current day is selected
  const filterPastTimeOptions = (timeOptions) => {
    // If not current day or no timezone selected, return all options
    if (!isCurrentDay(selectedDate) || !selectedTimezone) {
      return timeOptions;
    }

    const currentTime = moment().tz(selectedTimezone.key);
    const currentHour = currentTime.hour();
    const currentMinute = currentTime.minute();

    // Filter out time options that are in the past
    return timeOptions.filter((option) => {
      const [hours, minutes] = option.value.split(':').map(Number);
      if (hours < currentHour) return false;
      if (hours === currentHour && minutes <= currentMinute) return false;
      return true;
    });
  };

  const { dateFormat } = useDateFormat();
  const date_format = moment.parseZone(value?.formattedTime).format(date_fmt);
  const parsedDateForDatePicker =
    dateFormat?.label === 'DD/MM/YYYY'
      ? convertDateFormatForDatePicker(date_format)
      : date_format;
  useEffect(() => {
    if (!time_zones) return;
    if (meta?.value === undefined) {
      // default case
      setSelectedDate(moment().toDate());
      setSelectedTime(getCurrentTimePlus30());
    } else {
      let { formattedTime, time, timezoneValue: timezone } = meta?.value;
      if (typeof timezone === 'string') {
        // which means data is coming from server
        let objectKeys = Object.keys(time_zones);
        objectKeys.some((itr) => {
          if (itr === timezone) {
            timezone = {
              key: itr,
              label: `(GMT${time_zones[itr]} ${itr})`,
            };
            return true;
          }
          return false;
        });
        time = getCurrentTimePlus30(formattedTime);

        setSelectedDate(
          moment(moment.parseZone(formattedTime).format('MM-DD-YYYY')).toDate()
        );
        setSelectedTimezone(timezone);
        setSelectedTime(time);
      } else {
        setSelectedDate(moment(formattedTime).toDate());
        setSelectedTimezone(timezone);
        setSelectedTime(time);
      }
    }
    // eslint-disable-next-line
  }, [time_zones]);

  return (
    <Box>
      <div className="custom-ats-datepicker-wrapper date-field-with-timezone-wrapper">
        <InputGroup>
          <DayPicker
            weekStartsOn={1}
            required={isRequired}
            fromDate={disablePreviousDate ? new Date() : undefined}
            id={name}
            today={selectedDate}
            DateTimeFormat={getDateFormat(date_fmt)}
            mode="single"
            className={`ats-jobs-date-input-field ${className || ''}`}
            disabled={disablePreviousDate ? disablePreviousDates : undefined}
            numberOfMonths={1}
            defaultMonth={
              value ? new Date(parsedDateForDatePicker) : new Date()
            }
            selected={selectedDate}
            onSelect={handleDateChange}
            classNames={{
              caption: 'custom-caption',
              caption_label: 'custom-caption-label',
              nav_button: 'custom-nav-button',
              nav_button_previous: 'custom-nav-button-previous',
              nav_button_next: 'custom-nav-button-next',
            }}
          />
        </InputGroup>
      </div>
      {enableTime && (
        <Flex flexDir="column" gap="6px">
          <CustomTimeSelectorTz
            date={selectedDate}
            value={selectedTime}
            onChange={handleTimeChange}
            timeFormat={getTimeFormat()}
            metaData={value}
            isCurrentDay={isCurrentDay(selectedDate)}
            filterTimeOptions={filterPastTimeOptions}
            timeZone={selectedTimezone}
          />
          <TimeZoneSelectorV1
            value={selectedTimezone}
            onChange={handleTimeZoneChange}
          />
          {!dateWrapper?.isFutureDate && showError && (
            <KText
              pt="sm"
              color={'pastel-1-dark'}
              size="sm-medium"
              position={'relative'}
            >
              Please select future date and time
            </KText>
          )}
        </Flex>
      )}
    </Box>
  );
};
